import { cn } from "@au-schein-de/au-ui-library";
import type { FormatedProductPrice } from "@dransay/api";
import { useQueryState } from "nuqs";
import {
	Fragment,
	type ReactNode,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import { useFetcher, useNavigation, useRouteLoaderData } from "react-router";
import { InfiniteScroll } from "~/components/infinite-scroll.component";
import { ProductCarousel } from "~/components/product-carousel.component";
import { useVendorSelection } from "~/contexts/vendor-selection.context";
import { useCurrency } from "~/hooks/use-currency-price.hook";
import { useDebouncedCallback } from "~/hooks/use-debounced-callback.hook";
import { useDeepCompareEffect } from "~/hooks/use-deep-compare-effect";
import { useUpdateEffect } from "~/hooks/use-update-effect";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import {
	noResultsSearchedEvent,
	productListFilteredEvent,
	productListViewedEvent,
	productsSearchedEvent,
} from "~/tooling/telemetry/events";
import { VENDOR_PARAMS } from "~/utils/vendor";
import type { Route } from "../../routes/+types/_storefront._products.products";
import { useCart } from "../cart/cart.context";
import { ProductCardSkeleton } from "./card/product-card-skeleton.component";
import { usePdlExclusiveProducts } from "./exclusive-products/use-pdl-exclusive-products.hook";
import { ActiveFilters } from "./filters/active-filters.component";
import { FILTER_REQUEST_DEBOUNCE_MS } from "./filters/constants";
import type {
	FacetCounts,
	FiltersConfig,
	FiltersState,
	RemoveFilterFn,
} from "./filters/types";
import {
	getActiveFilterGroupsCount,
	hasFiltersBeyondTopProducersPreset,
} from "./filters/utils";
import { FiltersDrawer } from "./product-list.filters-drawer.component";
import { SortBy } from "./product-list.sort-by.component";
import { useOrderQuery } from "./use-order-query";
import { useSortQuery } from "./use-sort-query";

export type ProductListProps = {
	products: FormatedProductPrice[];
	renderItem: (item: FormatedProductPrice, index: number) => ReactNode;
	count: number;
	/**
	 * Server-computed unfiltered count for the current vendor/delivery
	 * combination — the denominator in "X of Y products match your search".
	 * When provided, the count text renders the long form on first paint
	 * instead of flipping after the totalCountFetcher resolves.
	 */
	initialTotalCount?: number;
	filters: FiltersState;
	filtersConfig: FiltersConfig;
	onFiltersChange: (filters: FiltersState, clearedAllFilters: boolean) => void;
	onFilterRemove: RemoveFilterFn;
	onClearAllFilters: () => void;
	title?: string;
	useFiltersV2: boolean;
	facets?: FacetCounts;
	onTopProducerRemoved?: () => void;
	hideMobileFilterSortControls?: boolean;
};

const ProductInfiniteScroll = InfiniteScroll<FormatedProductPrice>;

export const PRODUCTS_PER_PAGE = 18;

const parsePharmacyName = (name: string) => {
	// More robust regex that handles edge cases
	const match = name.match(/^(.+?)\s*\(\s*=\s*(.+?)\s*\)$/);
	if (match) {
		const primary = match[1].trim();
		const secondary = match[2].trim();

		// Validate that both parts are non-empty
		if (primary && secondary) {
			return { primary, secondary };
		}
	}
	return { primary: name, secondary: null };
};

export function ProductList({
	products,
	renderItem,
	count: initialCount,
	initialTotalCount,
	filters,
	filtersConfig,
	onFiltersChange,
	onFilterRemove,
	onClearAllFilters,
	title,
	useFiltersV2,
	facets,
	onTopProducerRemoved,
	hideMobileFilterSortControls = false,
}: ProductListProps) {
	const { items: cartItems } = useCart();
	const { t } = useTranslation("product");
	const { selectedVendorId, selectedDeliveryMethodKind } = useVendorSelection();

	// While a pharmacy switch is in flight, React Router keeps the current
	// route (and its now-stale product grid) mounted until the new vendor's
	// loader resolves. Those stale cards carry the *previous* vendorId, so a
	// click during this window opens a product for the wrong pharmacy and the
	// resulting order is misassigned (MKP-1685). Detect a pending navigation
	// whose target vendor differs from the active one and disable the grid
	// until the new vendor's products are loaded.
	const navigation = useNavigation();
	const pendingVendorId = navigation.location
		? new URLSearchParams(navigation.location.search).get(
				VENDOR_PARAMS.vendorId,
			)
		: null;
	const isSwitchingVendor =
		navigation.state === "loading" &&
		pendingVendorId !== null &&
		pendingVendorId !== (selectedVendorId ?? null);

	const hasActiveFilters = getActiveFilterGroupsCount(filters) > 0;
	const [currentCount, setCurrentCount] = useState(initialCount);
	const [isListReloading, setIsListReloading] = useState(false);
	const [search] = useQueryState("search");
	const [sort] = useSortQuery();
	const [order] = useOrderQuery();
	const loaderData = useRouteLoaderData<Route.ComponentProps["loaderData"]>(
		"routes/_storefront._products.products",
	);
	const currency = useCurrency();

	const debouncedSearchTracking = useDebouncedCallback(
		(search, productsCount) => {
			if (!search) {
				return;
			}

			captureEvent(productsSearchedEvent(search));

			if (!productsCount) {
				captureEvent(noResultsSearchedEvent("product", search));
			}
		},
		{ leading: false, delay: 1000 },
	);

	// Fetcher for total count (same pattern as FiltersDrawer)
	const totalCountFetcher = useFetcher();

	// Skip the initial total-count fetch — the loader already provides
	// `initialTotalCount` for SSR, so refetching it on mount is wasteful
	// and used to cause the post-hydration text flip in the count label.
	// Only fetch when the user changes vendor/delivery/filter state after
	// hydration.
	const isInitialTotalCountFetch = useRef(true);
	// Request key of the latest `.load()` call — used at render time to
	// decide whether `totalCountFetcher.data` still corresponds to the
	// current vendor/delivery/filters state. Without this guard, a stale
	// fetcher payload from a previous same-route navigation could mask a
	// fresh value and the "X of Y" label would flash the old denominator.
	const currentTotalCountRequestKey = `${selectedVendorId ?? ""}|${selectedDeliveryMethodKind ?? ""}|${hasActiveFilters ? "1" : "0"}`;
	const lastTotalCountRequestKeyRef = useRef<string | null>(null);
	const loadTotalCount = useDebouncedCallback(
		(url: string, requestKey: string) => {
			lastTotalCountRequestKeyRef.current = requestKey;
			totalCountFetcher.load(url);
		},
		{ delay: FILTER_REQUEST_DEBOUNCE_MS, leading: false },
	);
	useEffect(() => {
		if (isInitialTotalCountFetch.current) {
			isInitialTotalCountFetch.current = false;
			return;
		}
		if (hasActiveFilters) {
			// Load products' count scoped to vendor/delivery only.
			const searchParams = new URLSearchParams(window.location.search);
			searchParams.delete("filters");
			searchParams.delete("search");
			searchParams.set("page", "0");
			searchParams.set("pageSize", "1");

			if (selectedVendorId) {
				searchParams.set("vendorId", selectedVendorId);
			} else {
				searchParams.delete("vendorId");
			}

			if (selectedDeliveryMethodKind) {
				searchParams.set("deliveryMethod", selectedDeliveryMethodKind);
			} else {
				searchParams.delete("deliveryMethod");
			}

			loadTotalCount(
				`/api/products?${searchParams.toString()}`,
				currentTotalCountRequestKey,
			);
		} else {
			loadTotalCount.cancel();
		}
	}, [
		hasActiveFilters,
		selectedVendorId,
		selectedDeliveryMethodKind,
		loadTotalCount,
		currentTotalCountRequestKey,
	]);

	// Trust `fetcher.data.count` only when the fetcher is idle and its
	// latest request matches the current vendor/delivery/filters state;
	// otherwise fall back to the SSR-provided `initialTotalCount`.
	const totalCountFetcherIsFresh =
		totalCountFetcher.state === "idle" &&
		lastTotalCountRequestKeyRef.current === currentTotalCountRequestKey;
	const resolvedTotalCount = totalCountFetcherIsFresh
		? (totalCountFetcher.data?.count ?? initialTotalCount)
		: initialTotalCount;

	useUpdateEffect(() => {
		debouncedSearchTracking(search, currentCount);
	}, [search, currentCount, debouncedSearchTracking]);

	useDeepCompareEffect(
		() => {
			requestAnimationFrame(() => {
				captureEvent(
					productListFilteredEvent({ filters, sort, order, filtersConfig }),
				);
			});
		},
		[filters, sort, order],
		useUpdateEffect,
	);

	const { products: carouselProducts } = usePdlExclusiveProducts({
		initialProducts: useMemo(
			() => loaderData?.carouselProducts ?? [],
			[loaderData?.carouselProducts],
		),
		vendorId: selectedVendorId,
		deliveryMethodKind: selectedDeliveryMethodKind,
		filters,
	});

	const getTitle = () => {
		if (search) {
			return t("search.results.header", { search, count: currentCount });
		}

		return title ?? t("allProducts");
	};

	const parsedPharmacyName = useMemo(() => {
		if (!title) return { primary: "", secondary: null };
		return parsePharmacyName(title);
	}, [title]);

	const showCarousel = useMemo(() => {
		return !search && currentCount > 0 && cartItems.length === 0;
	}, [search, currentCount, cartItems]);

	const carouselPromotedProduct = useMemo(
		() =>
			hasFiltersBeyondTopProducersPreset(filters)
				? undefined
				: loaderData?.promotedProduct,
		[filters, loaderData?.promotedProduct],
	);

	const trackProductListViewedEvent = useDebouncedCallback(
		(items, listParams, currency, startPosition) => {
			captureEvent(
				productListViewedEvent(
					items,
					listParams,
					currency,
					startPosition,
					filtersConfig,
				),
			);
		},
		{ leading: false },
	);

	return (
		<>
			<div className="hidden md:flex items-center justify-between p-1">
				<div className="flex flex-col gap-1">
					<div className="flex flex-col">
						{search ? (
							<h1
								className="text-h4 md:text-h3 text-gray-700 inline"
								data-testid="vendor-title-on-products"
							>
								{getTitle()}
							</h1>
						) : (
							<>
								<h1
									className="text-h3 text-gray-900"
									data-testid="vendor-title-on-products"
								>
									{parsedPharmacyName.primary || getTitle()}
								</h1>
								{parsedPharmacyName.secondary && (
									<p className="text-body-bold text-gray-700">
										{parsedPharmacyName.secondary}
									</p>
								)}
							</>
						)}
					</div>
				</div>

				<div className="flex-shrink-0">
					<SortBy />
				</div>
			</div>

			<ActiveFilters
				filters={filters}
				config={filtersConfig}
				onRemove={onFilterRemove}
				onClearAll={onClearAllFilters}
				onTopProducerRemoved={onTopProducerRemoved}
			/>

			<div className="mb-2 mt-6 md:mt-4">
				{!isListReloading &&
					(hasActiveFilters && resolvedTotalCount !== undefined ? (
						<span
							className="text-body-sm text-gray-700 whitespace-nowrap"
							data-testid="products-count"
						>
							<Trans
								i18nKey="filteredProductsCount"
								ns="product"
								values={{
									currentCount,
									totalCount: resolvedTotalCount,
								}}
								components={{
									bold: <span className="text-body-sm-emphasis" />,
								}}
							/>
						</span>
					) : (
						<span
							className="text-body-sm-emphasis text-gray-700 whitespace-nowrap"
							data-testid="products-count"
						>
							{currentCount}
							<span className="text-body-sm"> {t("products")}</span>
						</span>
					))}
			</div>

			{showCarousel ? (
				<ProductCarousel
					promotedProduct={carouselPromotedProduct}
					products={carouselProducts}
				/>
			) : null}

			<div
				// `inert` blocks all pointer/keyboard interaction with the stale
				// cards during a pharmacy switch (prevents MKP-1685); the dimming
				// and wait cursor signal the in-flight load.
				inert={isSwitchingVendor}
				aria-busy={isSwitchingVendor}
				data-testid="product-grid"
				className={cn(
					"transition-opacity duration-200",
					isSwitchingVendor && "pointer-events-none cursor-wait opacity-60",
				)}
			>
				<ProductInfiniteScroll
					initialItems={products}
					containerClassName="grid grid-cols-2 gap-4 lg:grid-cols-3 sm:gap-6 md:pb-20 overflow-x-hidden md:mt-2"
					renderItem={renderItem}
					endpoint="/api/products"
					pageSize={PRODUCTS_PER_PAGE}
					itemsProperty="products"
					renderEmptyState={() => (
						<div className="mt-2" data-testid="no-products-found">
							<div className="mb-4 bg-white p-4 rounded-lg space-y-1">
								<div className="text-body-sm-emphasis text-gray-700">
									{search ? t("search.noResults.title") : t("noProductsFound")}
								</div>
								<div className="text-body-sm text-gray-700">
									{t("noProductsFoundDescription")}
								</div>
							</div>
							<ProductCarousel
								promotedProduct={carouselPromotedProduct}
								products={carouselProducts}
							/>
						</div>
					)}
					renderLoadingState={() => (
						<>
							{Array.from(
								{ length: PRODUCTS_PER_PAGE },
								(_, index) => index,
							).map((skeletonId) => (
								<Fragment key={`skeleton-${skeletonId}`}>
									<ProductCardSkeleton />
								</Fragment>
							))}
						</>
					)}
					onCountChange={setCurrentCount}
					onReloadingChange={setIsListReloading}
					onItemsLoaded={(items, listParams, startPosition) => {
						if (!items.length) return;

						requestAnimationFrame(() => {
							trackProductListViewedEvent(
								items,
								listParams,
								currency,
								startPosition,
							);
						});
					}}
				/>
			</div>

			{!hideMobileFilterSortControls && (
				<FiltersDrawer
					filters={filters}
					config={filtersConfig}
					onFilterChange={onFiltersChange}
					useFiltersV2={useFiltersV2}
					facets={facets}
					onTopProducerWarning={onTopProducerRemoved}
				/>
			)}
		</>
	);
}
